import { ARCHETYPE_LABELS, CARDS, EFFECTS, effectiveStat, type CardDefinition, type CardInstance, type FieldPlayer, type GameCommand, type PlayerSide, type PublicGameState, type TacticRarity } from "@soccertcg/game-engine";
import type { CSSProperties, ReactNode } from "react";
import { holoMove, holoLeave } from "./holo.ts";

type ActionType = GameCommand["type"];

/**
 * Canonical card face: designed once at 240×336 and scaled per context via
 * the `.card-fit` wrapper (`--card-s`), so small cards keep full fidelity.
 * Holo treatment is rarity-driven, ported from pokemon-cards-css:
 *   rookie   → glare only
 *   elite    → rainbow "reverse holo" sheet
 *   legend   → cosmos/galaxy foil
 *   champion → radiant gold foil (goalkeepers)
 * Tactics explicitly map common/rare/legendary onto rookie/elite/legend frames.
 */

export type Rarity = "rookie" | "elite" | "legend" | "champion";

const TACTIC_FRAME: Record<TacticRarity, Rarity> = {
  common: "rookie",
  rare: "elite",
  legendary: "legend",
};

export function rarityOf(def: CardDefinition): Rarity {
  if (def.role === "goalkeeper") return "champion";
  if (def.role === "tactic") return TACTIC_FRAME[def.rarity ?? "common"];
  return def.substitutions === 2 ? "legend" : def.substitutions === 1 ? "elite" : "rookie";
}

function effectText(def: CardDefinition): string {
  return def.effects.map(id => EFFECTS[id]).filter(Boolean).map(effect => `${effect!.name}: ${effect!.text}`).join("\n");
}

function StatIcon({ k }: { k: string }) {
  switch (k) {
    case "P": // power bolt
      return <svg viewBox="0 0 14 16" aria-hidden="true"><path d="M8.6 0 1.4 9.2h4.1L4.6 16l7.9-9.8H8.2z"/></svg>;
    case "D": // dribble ball
      return <svg viewBox="0 0 14 16" aria-hidden="true"><circle cx="7" cy="8" r="6.4"/><path className="ic-cut" d="M7 4.6 10.3 7l-1.26 3.9H4.96L3.7 7z"/></svg>;
    case "S": // steal swipe
      return <svg viewBox="0 0 14 16" aria-hidden="true"><path d="M1.2 13.9 11.8 3.3l1.1 1.1L2.3 15zM1 8.3 6.9 2.4 8 3.5 2.1 9.4zM6.7 14.1l5.9-5.9 1.1 1.1-5.9 5.9z"/></svg>;
    default: // DEF shield
      return <svg viewBox="0 0 14 16" aria-hidden="true"><path d="M7 0l6.2 2.1v5.2c0 4-2.6 6.6-6.2 8.5C3.4 13.9.8 11.3.8 7.3V2.1z"/></svg>;
  }
}

function CardFace({ def, hp, stats, side }: {
  def: CardDefinition;
  hp: number;
  stats: Array<{ key: string; value: number }>;
  side?: PlayerSide;
}) {
  const effects = def.effects.map(id => EFFECTS[id]).filter(Boolean);
  return <span className="gc-rotator" aria-hidden="true">
    <span className="gc-inner">
      <span className="gc-art" />
      <span className={`gc-rail ${side ?? ""}`} />
      <span className={`gc-topbar ${def.role === "tactic" ? "gc-topbar-tactic" : ""}`}>
        {def.role === "tactic" && <i className="gc-type">TACTIC</i>}
        {def.role === "field" && def.substitutions > 0 && <i className="gc-cost" title={`Requires ${def.substitutions} substitution${def.substitutions > 1 ? "s" : ""}`}>{def.substitutions}</i>}
        <strong className="gc-name">{def.name}</strong>
        {def.role !== "tactic" && <span className="gc-hp"><b>{hp}</b><small>HP</small></span>}
      </span>
      {stats.length > 0 && <span className="gc-stats">
        {stats.map(stat => <i key={stat.key} data-stat={stat.key}><StatIcon k={stat.key}/><b>{stat.value}</b></i>)}
      </span>}
      {effects.length > 0 && <span className="gc-lower">
        {def.archetype && <span className={`gc-archetype gc-archetype-${def.archetype}`}>{ARCHETYPE_LABELS[def.archetype]}</span>}
        <span className={`gc-text ${effects.length > 1 ? "gc-text-dense" : ""}`}>
          {effects.map(effect => <span className="gc-rule" key={effect!.id}><b>{effect!.name}.</b> {effect!.text}</span>)}
        </span>
      </span>}
    </span>
    <span className="gc-shine" />
    <span className="gc-glare" />
  </span>;
}

function cardClass(base: string, rarity: Rarity, side?: PlayerSide, extra = "") {
  return `pcard ${base} rarity-${rarity} ${side ?? ""} ${extra}`.trim();
}

const artStyle = (def: CardDefinition) => ({ "--card-art": `url(${def.art})` }) as CSSProperties;

/** Fixed-footprint wrapper: reserves scaled space, card renders full-size inside. */
export function CardFit({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <span className={`card-fit ${className}`}>{children}</span>;
}

export function HandCard({ card, side, selected, onClick }: { card: CardInstance; side: PlayerSide; selected?: boolean; onClick: () => void }) {
  const def = CARDS[card.definitionId]!;
  return <CardFit>
    <button
      type="button"
      className={cardClass("hand-pcard", rarityOf(def), side, selected ? "selected" : "")}
      onClick={onClick}
      onPointerMove={holoMove}
      onPointerLeave={holoLeave}
      title={effectText(def) || def.name}
      aria-label={`${def.name}${selected ? ", selected" : ""}`}
      data-card-id={card.instanceId}
      style={artStyle(def)}
    >
      <CardFace def={def} hp={def.health} side={side} stats={def.role === "tactic" ? [] : [
        { key: "P", value: def.power }, { key: "D", value: def.dribble }, { key: "S", value: def.steal }, { key: "DEF", value: def.defense },
      ]}/>
    </button>
  </CardFit>;
}

export function PlayerCard({ player, state, selected, hasBall, onClick, compact = false, action, targeted }: { player: FieldPlayer; state?: PublicGameState; selected?: boolean; hasBall?: boolean; onClick?: () => void; compact?: boolean; action?: ActionType; targeted?: boolean }) {
  const def = CARDS[player.definitionId]!;
  const stat = (name: "power" | "dribble" | "steal" | "defense") => state ? effectiveStat(state, player, name) : def[name];
  return <CardFit>
    <button
      className={cardClass("field-pcard", rarityOf(def), player.controller, `${selected ? "selected" : ""} ${player.tapped ? "tapped" : ""} ${compact ? "compact" : ""} ${hasBall ? "has-ball" : ""} ${action ? `acting action-${action}` : ""} ${targeted ? "targeted" : ""}`)}
      onClick={onClick}
      onPointerMove={holoMove}
      onPointerLeave={holoLeave}
      title={effectText(def)}
      style={artStyle(def)}
    >
      <CardFace def={def} hp={player.health} side={player.controller} stats={[
        { key: "P", value: stat("power") }, { key: "D", value: stat("dribble") }, { key: "S", value: stat("steal") }, { key: "DEF", value: stat("defense") },
      ]}/>
      {hasBall && <span className="possession-ring" aria-label="Has possession" />}
      {player.tapped && <span className="tap-label">TAPPED</span>}
    </button>
  </CardFit>;
}

export function GoalkeeperCard({ side, state, action, targeted, selected, onClick }: { side: PlayerSide; state: PublicGameState; action?: ActionType; targeted?: boolean; selected?: boolean; onClick?: () => void }) {
  const keeper = state.teams[side]?.goalkeeper;
  if (!keeper) return <div className="goalkeeper-card empty">Waiting for goalkeeper</div>;
  const def = CARDS[keeper.definitionId]!;
  const hasBall = state.ball.kind === "goalkeeper" && state.ball.side === side;
  return <CardFit>
    <div
      className={cardClass("keeper-pcard", "champion", side, `${selected ? "selected" : ""} ${keeper.tapped ? "tapped" : ""} ${hasBall ? "has-ball" : ""} ${action ? `acting action-${action}` : ""} ${targeted ? "targeted" : ""}`)}
      title={effectText(def)}
      onClick={onClick}
      onPointerMove={holoMove}
      onPointerLeave={holoLeave}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? event => { if (event.key === "Enter" || event.key === " ") onClick(); } : undefined}
      style={artStyle(def)}
    >
      <CardFace def={def} hp={keeper.health} side={side} stats={[
        { key: "DEF", value: def.defense }, { key: "D", value: def.dribble },
      ]}/>
      {hasBall && <span className="possession-ring" />}
      {keeper.tapped && <span className="tap-label">TAPPED</span>}
    </div>
  </CardFit>;
}

/** Definition-driven card for the deck builder collection. */
export function DefCard({ def, selected, onClick }: { def: CardDefinition; selected?: boolean; onClick?: () => void }) {
  const isKeeper = def.role === "goalkeeper";
  return <CardFit>
    <button
      type="button"
      className={cardClass("def-pcard", rarityOf(def), undefined, selected ? "selected" : "")}
      onClick={onClick}
      onPointerMove={holoMove}
      onPointerLeave={holoLeave}
      title={effectText(def) || def.name}
      style={artStyle(def)}
    >
      <CardFace def={def} hp={def.health} stats={def.role === "tactic" ? [] : isKeeper
        ? [{ key: "DEF", value: def.defense }, { key: "D", value: def.dribble }]
        : [{ key: "P", value: def.power }, { key: "D", value: def.dribble }, { key: "S", value: def.steal }, { key: "DEF", value: def.defense }]
      }/>
    </button>
  </CardFit>;
}
